import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Eye, EyeOff, Save, Send, X, FileText, History } from "lucide-react";
import client from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AdminLayout from "./AdminLayout";
import RichTextEditor from "../../components/admin/RichTextEditor";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

const ORDER = ["privacy", "terms", "disclaimer", "cookie", "donation"];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const StatusBadge = ({ status }) =>
  status === "Published" ? (
    <span className="rounded-sm bg-maroon px-2 py-0.5 text-[10px] uppercase tracking-wide text-ivory" data-testid="legal-status-badge">Published</span>
  ) : (
    <span className="rounded-sm border border-gold/40 bg-ivory px-2 py-0.5 text-[10px] uppercase tracking-wide text-ink-muted" data-testid="legal-status-badge">Draft — Not Public</span>
  );

export default function LegalManager() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "content_admin"].includes(user?.role);
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(false);
  const [audit, setAudit] = useState([]);

  const load = () =>
    client.get("/admin/legal").then(({ data }) => {
      const sorted = [...data].sort((a, b) => ORDER.indexOf(a.slug) - ORDER.indexOf(b.slug));
      setPages(sorted);
    }).catch(() => {});
  useEffect(() => { load(); }, []);

  const loadAudit = (slug) => client.get(`/admin/legal/${slug}/audit`).then(({ data }) => setAudit(data)).catch(() => setAudit([]));

  const open = (p) => {
    client.get(`/admin/legal/${p.slug}`).then(({ data }) => {
      setEditing({ ...data });
      setPreview(false);
      loadAudit(p.slug);
    }).catch(() => toast.error("Could not open page."));
  };

  const persist = async (status) => {
    const payload = {
      title: editing.title,
      content: editing.content,
      metaTitle: editing.metaTitle,
      metaDescription: editing.metaDescription,
    };
    if (status) payload.status = status;
    try {
      const { data } = await client.put(`/admin/legal/${editing.slug}`, payload);
      toast.success(status === "Published" ? "Published — now publicly visible." : status === "Draft" ? "Saved as draft — hidden from public." : "Updated.");
      setEditing({ ...editing, ...data });
      loadAudit(editing.slug);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    }
  };

  return (
    <AdminLayout title="Legal & Policies">
      <p className="mb-6 max-w-2xl text-sm text-ink-muted">
        Manage the Trust's legal pages. Only <strong>Published</strong> pages are visible on the public website; drafts stay private. Use the editor to format content without writing any code.
      </p>

      <div className="grid gap-4 md:grid-cols-2" data-testid="legal-list">
        {pages.map((p) => (
          <div key={p.slug} className="rounded-md border border-gold/20 bg-white p-5" data-testid={`legal-card-${p.slug}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-gold-dark" />
                <h3 className="font-medium text-maroon">{p.title}</h3>
              </div>
              <StatusBadge status={p.status} />
            </div>
            {p.isPlaceholder && <p className="mt-2 text-xs italic text-gold-dark">Using placeholder / default content</p>}
            <p className="mt-3 text-xs text-ink-muted">Last updated: {fmt(p.updatedAt)}</p>
            <p className="text-xs text-ink-muted">Last modified by: {p.lastModifiedBy || "—"}</p>
            <div className="mt-4">
              <button onClick={() => open(p)} data-testid={`legal-edit-${p.slug}`}
                className="inline-flex items-center gap-1.5 text-xs text-maroon hover:underline">
                {canEdit ? <><Pencil size={13} /> Edit</> : <><Eye size={13} /> View</>}
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-md bg-white p-7" onClick={(e) => e.stopPropagation()} data-testid="legal-editor">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-serif text-2xl text-maroon">{editing.title}</h2>
              <div className="flex items-center gap-3">
                <StatusBadge status={editing.status} />
                <button onClick={() => setEditing(null)} data-testid="legal-close" className="text-ink-muted hover:text-maroon"><X size={20} /></button>
              </div>
            </div>

            <div className="mb-3 flex gap-2">
              <button onClick={() => setPreview(false)} data-testid="legal-tab-edit"
                className={`rounded-sm px-3 py-1.5 text-xs uppercase tracking-wide ${!preview ? "bg-maroon text-ivory" : "border border-gold/40 text-maroon"}`}>Edit</button>
              <button onClick={() => setPreview(true)} data-testid="legal-tab-preview"
                className={`inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-xs uppercase tracking-wide ${preview ? "bg-maroon text-ivory" : "border border-gold/40 text-maroon"}`}><Eye size={13} /> Preview</button>
            </div>

            {preview ? (
              <div className="legal-content min-h-[240px] rounded-sm border border-gold/20 bg-ivory/40 p-5 text-sm leading-relaxed text-ink-muted" data-testid="legal-preview"
                dangerouslySetInnerHTML={{ __html: editing.content || "<p>No content.</p>" }} />
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>Page Title</Label>
                  <Input className="mt-1.5" value={editing.title || ""} disabled={!canEdit}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="legal-title" />
                </div>
                <div>
                  <Label>Content</Label>
                  <div className="mt-1.5">
                    {canEdit
                      ? <RichTextEditor value={editing.content} onChange={(html) => setEditing({ ...editing, content: html })} />
                      : <div className="legal-content rounded-sm border border-gold/20 p-4 text-sm text-ink-muted" dangerouslySetInnerHTML={{ __html: editing.content || "" }} />}
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label>SEO — Meta Title</Label>
                    <Input className="mt-1.5" value={editing.metaTitle || ""} disabled={!canEdit}
                      onChange={(e) => setEditing({ ...editing, metaTitle: e.target.value })} data-testid="legal-metatitle" />
                  </div>
                  <div>
                    <Label>SEO — Meta Description</Label>
                    <Textarea className="mt-1.5" rows={2} value={editing.metaDescription || ""} disabled={!canEdit}
                      onChange={(e) => setEditing({ ...editing, metaDescription: e.target.value })} data-testid="legal-metadesc" />
                  </div>
                </div>
              </div>
            )}

            {canEdit && (
              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={() => persist("Draft")} data-testid="legal-save-draft"
                  className="inline-flex items-center gap-1.5 border border-gold/40 px-5 py-2.5 text-xs uppercase tracking-wide text-maroon hover:bg-ivory"><Save size={14} /> Save Draft</button>
                <button onClick={() => persist()} data-testid="legal-update"
                  className="inline-flex items-center gap-1.5 border border-gold/40 px-5 py-2.5 text-xs uppercase tracking-wide text-maroon hover:bg-ivory"><Save size={14} /> Update</button>
                {editing.status === "Published" ? (
                  <button onClick={() => persist("Draft")} data-testid="legal-unpublish"
                    className="inline-flex items-center gap-1.5 border border-gold/40 px-5 py-2.5 text-xs uppercase tracking-wide text-maroon hover:bg-ivory"><EyeOff size={14} /> Unpublish</button>
                ) : (
                  <button onClick={() => persist("Published")} data-testid="legal-publish"
                    className="inline-flex items-center gap-1.5 bg-maroon px-5 py-2.5 text-xs uppercase tracking-wide text-ivory hover:bg-maroon-dark"><Send size={14} /> Publish</button>
                )}
              </div>
            )}

            {audit.length > 0 && (
              <div className="mt-7 border-t border-gold/20 pt-4">
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-ink-muted/70"><History size={14} /> Recent Activity</div>
                <ul className="space-y-1 text-xs text-ink-muted" data-testid="legal-audit">
                  {audit.map((a, i) => (
                    <li key={i}><span className="capitalize text-maroon">{a.action}</span> by {a.by} · {fmt(a.at)}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
